import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  Button,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeDown,
  VolumeOff,
  Refresh,
  Radio,
  Album,
  Headphones
} from '@mui/icons-material';
import { fixImageUrl } from '../../utils/imageUtils';

const ShoutcastPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [prevVolume, setPrevVolume] = useState(70);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamInfo, setStreamInfo] = useState('NostalBeat Radio - Carregando...');
  const [nowPlayingMeta, setNowPlayingMeta] = useState({
    coverUrl: '',
    djPhoto: '',
    isDjLive: false,
    artist: '',
    title: '',
    currentDj: '',
  });
  const [imageError, setImageError] = useState(false);
  const audioRef = useRef(null);

  const displayImage = nowPlayingMeta.isDjLive
    ? (nowPlayingMeta.djPhoto || nowPlayingMeta.coverUrl)
    : nowPlayingMeta.coverUrl;

  useEffect(() => {
    setImageError(false);
  }, [displayImage]);

  // ID persistente de sessão do ouvinte no site
  const sessionIdRef = useRef(
    typeof window !== 'undefined'
      ? (sessionStorage.getItem('nb_site_session') || (() => {
          const id = 'site_' + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
          try { sessionStorage.setItem('nb_site_session', id); } catch (e) {}
          return id;
        })())
      : 'site_' + Math.random().toString(36).substring(2, 11)
  );

  // URLs do servidor Icecast NostalBeat (/stream é o ponto público unificado)
  const STREAM_URLS = [
    '/stream',
    '/autodj',
    '/live',
  ];
  const [currentStreamIndex, setCurrentStreamIndex] = useState(0);

  // Enviar batimento cardíaco (Heartbeat Ping) para contabilizar ouvinte do site em tempo real
  useEffect(() => {
    if (!isPlaying) return;

    const sendPing = () => {
      fetch('/api/radio/listener-ping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          mountpoint: '/stream',
        }),
      }).catch(() => {});
    };

    sendPing();
    const pingTimer = setInterval(sendPing, 10000);

    const handleUnload = () => {
      try {
        const blob = new Blob([JSON.stringify({ sessionId: sessionIdRef.current })], { type: 'application/json' });
        navigator.sendBeacon('/api/radio/listener-disconnect', blob);
      } catch (e) {}
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(pingTimer);
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, [isPlaying]);

  // Buscar informações da música em tempo real da API NostalBeat
  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const res = await fetch('/api/radio/now-playing');
        if (res.ok) {
          const data = await res.json();
          let currentArtist = '';
          let currentTitle = '';

          if (data?.currentTrack) {
            const rawArtist = (data.currentTrack.artist || '').trim();
            const rawTitle = (data.currentTrack.title || '').trim();
            const cleanArtist = rawArtist.replace(/^(DJ\s+)+/i, 'DJ ').trim();
            const cleanTitle = rawTitle.replace(/^(DJ\s+)+/i, 'DJ ').trim();
            currentArtist = cleanArtist;
            currentTitle = cleanTitle;

            if (cleanArtist && cleanTitle) {
              if (cleanTitle.toLowerCase().startsWith(cleanArtist.toLowerCase())) {
                setStreamInfo(cleanTitle);
              } else {
                setStreamInfo(`${cleanArtist} - ${cleanTitle}`);
              }
            } else {
              setStreamInfo(cleanTitle || cleanArtist || 'NostalBeat Web Rádio');
            }
          } else if (data?.currentProgram) {
            setStreamInfo(`NostalBeat - ${data.currentProgram}`);
          }

          const isLive = Boolean(data?.isDjLive || data?.audioSource === 'live' || data?.currentTrack?.isLive);
          const rawDjPhoto = data?.djPhoto || data?.currentTrack?.djPhoto || '';
          let rawCover = data?.coverUrl || data?.currentTrack?.coverUrl || '';

          if (isLive && !rawCover) {
            rawCover = rawDjPhoto;
          }

          setNowPlayingMeta({
            isDjLive: isLive,
            djPhoto: rawDjPhoto ? fixImageUrl(rawDjPhoto) : '',
            coverUrl: rawCover ? fixImageUrl(rawCover) : '',
            artist: currentArtist,
            title: currentTitle,
            currentDj: data?.currentDj || '',
          });

          // Se for AutoDJ e a API ainda não tiver retornado capa, busca sob demanda
          if (!isLive && !rawCover && currentArtist) {
            fetch(`/api/radio/artwork?artist=${encodeURIComponent(currentArtist)}&title=${encodeURIComponent(currentTitle)}`)
              .then((r) => r.json())
              .then((art) => {
                if (art?.coverUrl) {
                  setNowPlayingMeta((prev) => ({ ...prev, coverUrl: fixImageUrl(art.coverUrl) }));
                }
              })
              .catch(() => {});
          }
        }
      } catch (e) {
        // Fallback silencioso
      }
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 4000);
    return () => clearInterval(interval);
  }, []);

  const tryNextStream = () => {
    const nextIndex = (currentStreamIndex + 1) % STREAM_URLS.length;
    setCurrentStreamIndex(nextIndex);
    console.log(`Trying next stream URL: ${STREAM_URLS[nextIndex]}`);
    return STREAM_URLS[nextIndex];
  };

  const togglePlay = () => {
    if (!audioRef.current) {
      console.error('Audio element not found');
      return;
    }

    const audio = audioRef.current;
    const isCurrentlyActive = isPlaying || isLoading || (!audio.paused && audio.currentTime > 0);

    if (isCurrentlyActive) {
      console.log('Parando e pausando áudio imediatamente...');
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.src = '';
        audio.load();
      } catch (e) {}

      setIsPlaying(false);
      setIsLoading(false);
      setError(null);

      // Desconecta sessão do ouvinte
      try {
        const blob = new Blob([JSON.stringify({ sessionId: sessionIdRef.current })], { type: 'application/json' });
        navigator.sendBeacon('/api/radio/listener-disconnect', blob);
      } catch (e) {}
    } else {
      setIsLoading(true);
      setError(null);

      audio.volume = volume / 100;
      const currentUrl = STREAM_URLS[currentStreamIndex] || '/stream';
      audio.src = `${currentUrl}?t=${Date.now()}`;
      audio.crossOrigin = 'anonymous';

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
          })
          .catch(error => {
            if (error.name === 'AbortError') return;
            console.error('Play error:', error);
            setIsPlaying(false);
            setIsLoading(false);
            setError('Clique para reproduzir o áudio');
          });
      }
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
      if (audioRef.current) audioRef.current.muted = false;
    }
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  const toggleMute = () => {
    if (isMuted) {
      const restored = prevVolume > 0 ? prevVolume : 70;
      setVolume(restored);
      setIsMuted(false);
      if (audioRef.current) {
        audioRef.current.volume = restored / 100;
        audioRef.current.muted = false;
      }
    } else {
      setPrevVolume(volume);
      setVolume(0);
      setIsMuted(true);
      if (audioRef.current) {
        audioRef.current.volume = 0;
        audioRef.current.muted = true;
      }
    }
  };

  const retryConnection = () => {
    setError(null);
    setIsPlaying(false);
    setIsLoading(false);
    
    if (audioRef.current) {
      const nextUrl = tryNextStream();
      audioRef.current.src = nextUrl;
      audioRef.current.load();
      console.log('Retrying connection to:', nextUrl);
    }
  };

  const handleError = (error) => {
    console.error('Shoutcast stream error:', error);
    console.error('Error details:', {
      error: error,
      src: audioRef.current?.src,
      networkState: audioRef.current?.networkState,
      readyState: audioRef.current?.readyState
    });
    
    let errorMessage = 'Erro de conexão com o servidor Shoutcast';
    if (error.target?.error) {
      const errorCode = error.target.error.code;
      switch (errorCode) {
        case 1:
          errorMessage = 'Erro de rede - servidor inacessível';
          break;
        case 2:
          errorMessage = 'Formato de áudio não suportado';
          break;
        case 3:
          errorMessage = 'Erro de decodificação';
          break;
        case 4:
          errorMessage = 'URL do stream inválida';
          break;
        default:
          errorMessage = `Erro de áudio (código: ${errorCode})`;
      }
    }
    
    setError(errorMessage);
    setIsLoading(false);
    setIsPlaying(false);
  };

  const handleLoadStart = () => {
    console.log('Audio load started');
    setIsLoading(true);
  };

  const handleCanPlay = () => {
    console.log('Audio can play');
    setIsLoading(false);
  };

  const handleLoadedData = () => {
    console.log('Audio data loaded');
    setIsLoading(false);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'rgba(10, 10, 14, 0.96)',
        backdropFilter: { xs: 'none', md: 'blur(16px)' },
        borderTop: '1px solid rgba(255, 107, 53, 0.3)',
        zIndex: 1100,
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: { xs: 1, sm: 1.4 },
        boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.7), 0 0 20px rgba(255, 107, 53, 0.1)',
        transform: 'translateZ(0)',
      }}
    >
      {/* Top Animated Neon Flow Line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: isPlaying
            ? 'linear-gradient(90deg, #FF6B35 0%, #00D4FF 35%, #8B5CF6 70%, #FF6B35 100%)'
            : 'linear-gradient(90deg, rgba(255, 107, 53, 0.3) 0%, rgba(0, 212, 255, 0.3) 100%)',
          backgroundSize: '200% 100%',
          animation: isPlaying ? 'flowNeon 4s linear infinite' : 'none',
          '@keyframes flowNeon': {
            '0%': { backgroundPosition: '0% 50%' },
            '100%': { backgroundPosition: '200% 50%' },
          },
        }}
      />

      {/* Loading indicator */}
      {isLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 2,
            left: 0,
            right: 0,
            height: 2,
            bgcolor: 'rgba(255, 107, 53, 0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#00D4FF',
            },
          }}
        />
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, py: 0.2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={retryConnection}
              startIcon={<Refresh />}
            >
              Reconectar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2, md: 3 },
          maxWidth: '1350px',
          mx: 'auto',
        }}
      >
        {/* Left Section: Cover/DJ Image & Play/Pause Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/* Foto do Artista ou DJ Tocando ao Vivo (substitui o disco de vinil) */}
          <Tooltip
            title={
              nowPlayingMeta.isDjLive
                ? `DJ Ao Vivo: ${nowPlayingMeta.currentDj || 'No Ar'}`
                : (nowPlayingMeta.artist ? `${nowPlayingMeta.artist} - ${nowPlayingMeta.title || 'Música'}` : 'NostalBeat Web Rádio')
            }
            arrow
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                borderRadius: '12px',
                overflow: 'hidden',
                flexShrink: 0,
                background: 'linear-gradient(135deg, #181d29 0%, #0d121d 100%)',
                border: nowPlayingMeta.isDjLive
                  ? '2px solid #ef4444'
                  : isPlaying
                  ? '2px solid #00D4FF'
                  : '2px solid rgba(255, 255, 255, 0.15)',
                boxShadow: nowPlayingMeta.isDjLive
                  ? '0 0 16px rgba(239, 68, 68, 0.55), 0 4px 10px rgba(0, 0, 0, 0.7)'
                  : isPlaying
                  ? '0 0 16px rgba(0, 212, 255, 0.45), 0 4px 10px rgba(0, 0, 0, 0.7)'
                  : '0 4px 10px rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}
            >
              {displayImage && !imageError ? (
                <Box
                  component="img"
                  src={displayImage}
                  alt={nowPlayingMeta.artist || (nowPlayingMeta.isDjLive ? nowPlayingMeta.currentDj : 'NostalBeat')}
                  onError={() => setImageError(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 0.35s ease',
                    '&:hover': { transform: 'scale(1.08)' },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    background: nowPlayingMeta.isDjLive
                      ? 'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)'
                      : 'linear-gradient(135deg, #00D4FF 0%, #007799 100%)',
                  }}
                >
                  {nowPlayingMeta.isDjLive ? (
                    <Headphones sx={{ color: '#FFF', fontSize: { xs: 22, sm: 26 } }} />
                  ) : (
                    <Album sx={{ color: '#FFF', fontSize: { xs: 22, sm: 26 } }} />
                  )}
                </Box>
              )}

              {/* Tag "AO VIVO" pulsante se for DJ ao vivo */}
              {nowPlayingMeta.isDjLive && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    right: 2,
                    bgcolor: 'rgba(239, 68, 68, 0.9)',
                    color: '#fff',
                    fontSize: '8px',
                    fontWeight: 900,
                    textAlign: 'center',
                    borderRadius: '4px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: '0.5px',
                    boxShadow: '0 0 6px #ef4444',
                  }}
                >
                  AO VIVO
                </Box>
              )}
            </Box>
          </Tooltip>

          {/* Play / Pause Neon Button */}
          <IconButton
            onClick={togglePlay}
            sx={{
              color: '#FFF',
              background: isPlaying
                ? 'linear-gradient(135deg, #FF6B35 0%, #E0531D 100%)'
                : 'linear-gradient(135deg, #00D4FF 0%, #0088A3 100%)',
              border: isPlaying ? '2px solid #FF8C5A' : '2px solid #33DDFF',
              boxShadow: isPlaying
                ? '0 0 16px rgba(255, 107, 53, 0.6)'
                : '0 0 16px rgba(0, 212, 255, 0.6)',
              width: { xs: 46, sm: 52 },
              height: { xs: 46, sm: 52 },
              '&:hover': {
                filter: 'brightness(1.15)',
                transform: 'scale(1.06)',
              },
              transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: '#FFF' }} />
            ) : isPlaying ? (
              <Pause sx={{ fontSize: { xs: '1.6rem', sm: '1.9rem' } }} />
            ) : (
              <PlayArrow sx={{ fontSize: { xs: '1.7rem', sm: '2rem' }, ml: 0.3 }} />
            )}
          </IconButton>
        </Box>

        {/* Center: Track Name & Dynamic Soundwave Equalizer */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0.5, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: '#FFF',
                fontFamily: 'Orbitron, sans-serif',
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.05rem' },
                letterSpacing: '0.3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: isPlaying ? '0 0 12px rgba(255, 107, 53, 0.4)' : 'none',
              }}
            >
              {streamInfo}
            </Typography>

            {/* Micro Equalizer Wave Bars */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                height: 16,
                gap: '2px',
                flexShrink: 0,
              }}
            >
              {[0.5, 1.0, 0.7, 1.2, 0.6].map((duration, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 3,
                    bgcolor: isPlaying
                      ? i % 2 === 0
                        ? '#FF6B35'
                        : '#00D4FF'
                      : 'rgba(255, 255, 255, 0.25)',
                    borderRadius: 1,
                    height: isPlaying ? '100%' : '3px',
                    animation: isPlaying
                      ? `equalizerWave ${duration}s ease-in-out infinite alternate`
                      : 'none',
                    '@keyframes equalizerWave': {
                      '0%': { height: '20%' },
                      '50%': { height: '100%' },
                      '100%': { height: '35%' },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Subtitle / Status Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <Chip
              icon={
                <Radio
                  sx={{
                    fontSize: '0.85rem !important',
                    color: nowPlayingMeta.isDjLive
                      ? '#ef4444 !important'
                      : isPlaying
                      ? '#00D4FF !important'
                      : '#FFF !important',
                  }}
                />
              }
              label={
                isPlaying
                  ? nowPlayingMeta.isDjLive
                    ? 'DJ AO VIVO NO AR'
                    : 'NO AR • AO VIVO'
                  : 'PAUSADO'
              }
              size="small"
              sx={{
                height: 20,
                bgcolor: nowPlayingMeta.isDjLive
                  ? 'rgba(239, 68, 68, 0.2)'
                  : isPlaying
                  ? 'rgba(0, 212, 255, 0.15)'
                  : 'rgba(255, 255, 255, 0.08)',
                border: nowPlayingMeta.isDjLive
                  ? '1px solid #ef4444'
                  : isPlaying
                  ? '1px solid #00D4FF'
                  : '1px solid rgba(255, 255, 255, 0.15)',
                color: nowPlayingMeta.isDjLive
                  ? '#f87171'
                  : isPlaying
                  ? '#00D4FF'
                  : 'rgba(255, 255, 255, 0.7)',
                fontWeight: 800,
                fontSize: '0.65rem',
                fontFamily: 'Orbitron, sans-serif',
                letterSpacing: '0.5px',
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              {nowPlayingMeta.isDjLive && nowPlayingMeta.currentDj
                ? `Ao Vivo com ${nowPlayingMeta.currentDj} • Estúdio NostalBeat`
                : 'NostalBeat Web Rádio • Sinta a batida'}
            </Typography>
          </Box>
        </Box>

        {/* Right Section: Volume Console */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Volume Control with 1-Click Mute */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '25px',
              px: 1.5,
              py: 0.4,
              minWidth: { sm: 140, md: 170 },
            }}
          >
            <Tooltip title={isMuted ? 'Desmutar' : 'Mudo'}>
              <IconButton
                size="small"
                onClick={toggleMute}
                sx={{
                  color: isMuted ? '#FF1744' : '#00D4FF',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(0, 212, 255, 0.15)',
                  },
                }}
              >
                {isMuted || volume === 0 ? (
                  <VolumeOff fontSize="small" />
                ) : volume < 50 ? (
                  <VolumeDown fontSize="small" />
                ) : (
                  <VolumeUp fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Slider
              value={isMuted ? 0 : volume}
              onChange={(event, newValue) => handleVolumeChange(newValue)}
              min={0}
              max={100}
              sx={{
                width: { sm: 60, md: 80 },
                color: '#FF6B35',
                '& .MuiSlider-track': {
                  background: 'linear-gradient(90deg, #FF6B35, #00D4FF)',
                  border: 'none',
                  height: 4,
                },
                '& .MuiSlider-rail': {
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  height: 4,
                },
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                  bgcolor: '#FFF',
                  border: '2px solid #FF6B35',
                  boxShadow: '0 0 8px rgba(255, 107, 53, 0.6)',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(255, 107, 53, 0.2)',
                  },
                },
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                minWidth: 26,
                textAlign: 'right',
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            >
              {isMuted ? '0%' : `${volume}%`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onError={handleError}
        onLoadStart={handleLoadStart}
        onCanPlay={handleCanPlay}
        onLoadedData={handleLoadedData}
        onPlay={() => console.log('Audio play event')}
        onPause={() => console.log('Audio pause event')}
        preload="none"
        controls={false}
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />

      {/* Centova Stream Info Element (hidden) */}
      <span 
        className="cc_streaminfo" 
        data-type="song" 
        data-username="fabiogarcia"
        style={{ display: 'none' }}
      >
        Carregando...
      </span>
    </Box>
  );
};

export default ShoutcastPlayer;
